import { apiClient } from "../api/ApiClient";
import type { KeyValue } from "../models/KeyValue";
import type { Order } from "../models/Order";
import { PagedResult } from "../models/PagedResult";
import { SearchParam } from "../models/SearchParam";
import { CreateCrudService } from "./CrudServiceFactory";

export function OrderService(warehouseCode?: string, unassigned = false) {
  const route = "order";
  const crudService = CreateCrudService<Order, KeyValue>({
    PaginationInPath: true,
    WarehouseCode: unassigned ? undefined : warehouseCode,
    BaseFilters: unassigned
      ? [
          {
            Field: "WareHouse",
            Operator: "IsNull",
          } as SearchParam["Filters"][number],
        ]
      : undefined,
    SaveRequest: async (rows) =>
      (
        await apiClient.post<Order[]>(
          "order/SaveOrder",
          rows.map((row) => ({
            ...(row as Order),
            WareHouse: (row as Order).WareHouse || null,
          })),
          { busyMessage: "Saving order..." },
        )
      ).data,
    SearchReferences: true,
    Route: "order",
    EntityName: "Order",
    DeleteAction: "DeleteOrder",
    PartialFields: ["Code", "CustomerName"],
    LoadingMessage: "Loading orders...",
    SavingMessage: "Saving order...",
    MapPartial: (value) => ({
      Key: String(value.Code ?? ""),
      Value: String(value.CustomerName ?? value.Code ?? ""),
    }),
  });

  const GetPayable = async (
    skip = 0,
    limit = 50,
  ): Promise<PagedResult<Order>> => {
    const response = await apiClient.post<PagedResult<Order>>(
      `${route}/Search/${skip}/${limit}`,
      {
        Filters: [
          ...(warehouseCode
            ? [{ Field: "WareHouse", Operator: "EQ", Value: warehouseCode }]
            : []),
          {
            Field: "Status",
            Operator: "EQ",
            Value: "Pending",
          },
        ],
      } as SearchParam,
      {
        busyMessage: "Loading active orders...",
        params: warehouseCode ? { warehouse: warehouseCode } : undefined,
      },
    );
    return response.data;
  };

  const GetForCustomer = async (
    customer: string,
    skip = 0,
    limit = 50,
  ): Promise<PagedResult<KeyValue>> => {
    const response = await apiClient.post<PagedResult<Record<string, unknown>>>(
      `${route}/SearchDynamic/${skip}/${limit}`,
      {
        Fields: ["Code", "TotalAmount"],
        Filters: [
          ...(warehouseCode
            ? [{ Field: "WareHouse", Operator: "EQ", Value: warehouseCode }]
            : []),
          {
            Field: "CustomerCode",
            Operator: "EQ",
            Value: customer,
          },
        ],
      } as SearchParam,
      {
        busyMessage: "Loading orders...",
      },
    );

    return {
      ...response.data,
      Data: response.data.Data.map((value) => ({
        Key: String(value.Code ?? ""),
        Value: String(value.Name ?? ""),
      })),
    };
  };

  return {
    ...crudService,
    GetForCustomer,
    GetPayable,
  };
}
